import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRinging, EnvelopeSimple, UsersThree, Plus, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  admin: 'bg-rose-100 text-rose-700',
  manager: 'bg-indigo-100 text-indigo-700',
  user: 'bg-zinc-100 text-zinc-600',
};
const ROLE_LABEL = { admin: '管理者', manager: 'マネージャー', user: '一般' };

export default function AdminNotifySettings() {
  const [users, setUsers] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState([]);
  const [extraInput, setExtraInput] = useState('');
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/users`), axios.get(`${API}/admin-notify`)])
      .then(([u, c]) => {
        const items = (Array.isArray(u.data) ? u.data : u.data.items || [])
          .filter((x) => x.email && ['admin', 'manager'].includes(x.role));
        setUsers(items);
        setUserIds(c.data.user_ids || []);
        setExtraEmails(c.data.extra_emails || []);
        setResolved(c.data.resolved_emails || []);
      })
      .catch(() => toast.error('読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setDirty(true);
  };

  const addExtra = () => {
    const e = extraInput.trim();
    if (!e || !e.includes('@')) { toast.error('有効なメールアドレスを入力してください'); return; }
    if (extraEmails.includes(e)) { setExtraInput(''); return; }
    setExtraEmails((prev) => [...prev, e]);
    setExtraInput('');
    setDirty(true);
  };

  const removeExtra = (e) => { setExtraEmails((prev) => prev.filter((x) => x !== e)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin-notify`, { user_ids: userIds, extra_emails: extraEmails });
      setResolved(res.data.resolved_emails || []);
      setDirty(false);
      toast.success('管理者通知の宛先を保存しました');
    } catch (e) {
      toast.error(e.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => ({
    admin: users.filter((u) => u.role === 'admin'),
    manager: users.filter((u) => u.role === 'manager'),
  }), [users]);

  if (loading) return <div className="p-8 text-sm text-zinc-400">読み込み中…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-notify-settings">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2"
          data-testid="settings-page-title">
          <BellRinging size={24} weight="duotone" className="text-teal-700" /> 管理者通知設定
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          システム通知メールを受け取る管理者を選択します。選択された人だけに送られるので、通知が多すぎる問題を防げます。
        </p>
        <ul className="text-xs text-zinc-400 mt-2 list-disc pl-5 space-y-0.5">
          <li>資材通知（依頼作成・デイリーダイジェスト）— 資材通知設定で個別の宛先が選ばれていない場合の既定の宛先になります</li>
          <li>勤怠フォーム通知 — 部署に宛先メールが未設定の場合のフォールバック宛先になります</li>
        </ul>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
          <UsersThree size={14} /> 通知を受け取るユーザー
        </p>
        {['admin', 'manager'].map((role) => grouped[role].length > 0 && (
          <div key={role} className="space-y-1.5">
            {grouped[role].map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 rounded-lg border border-zinc-100 hover:border-zinc-200 px-3 py-2 cursor-pointer transition-colors"
                data-testid={`admin-notify-user-${u.id}`}
              >
                <input
                  type="checkbox"
                  checked={userIds.includes(u.id)}
                  onChange={() => toggle(u.id)}
                  className="h-4 w-4 accent-sky-600"
                  data-testid={`admin-notify-check-${u.id}`}
                />
                <span className="text-sm text-zinc-800">{u.name || u.email}</span>
                <span className="text-xs text-zinc-400 font-mono">{u.email}</span>
                <Badge className={`ml-auto text-[10px] ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</Badge>
              </label>
            ))}
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-zinc-400">メールアドレスを持つ管理者/マネージャーがいません</p>}
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
          <EnvelopeSimple size={14} /> 追加の宛先（任意）
        </p>
        <div className="flex gap-2">
          <Input
            value={extraInput}
            onChange={(e) => setExtraInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtra(); } }}
            placeholder="mail@example.co.jp"
            className="h-9 max-w-xs"
            data-testid="admin-notify-extra-input"
          />
          <Button size="sm" variant="outline" onClick={addExtra} className="h-9" data-testid="admin-notify-extra-add">
            <Plus size={14} className="mr-1" /> 追加
          </Button>
        </div>
        {extraEmails.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {extraEmails.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-mono text-zinc-700">
                {e}
                <button type="button" onClick={() => removeExtra(e)} className="text-zinc-400 hover:text-rose-500" data-testid={`admin-notify-extra-remove-${e}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving || !dirty} className="bg-teal-700 hover:bg-teal-800" data-testid="admin-notify-save">
          {saving ? '保存中…' : '保存'}
        </Button>
        <span className="text-xs text-zinc-500" data-testid="admin-notify-resolved">
          現在の宛先 <span className="font-semibold text-zinc-700">{resolved.length}名</span>
          {resolved.length > 0 && <span className="text-zinc-400"> — {resolved.join(', ')}</span>}
        </span>
      </div>
    </div>
  );
}
